// People: everyone in the program, from its audience or invited by hand, with
// how far they are (layout from Ronan's capability prototype). Invite People
// adds anyone, audience or not: someone already in the organization, or
// someone new by email. Remove, on a person's row, takes them out of the
// program and keeps their progress (peopleRemoval.ts says who it is for).
// Ticking several rows removes them all at once.

import React, { useCallback, useEffect, useState } from 'react';
import { Box, Checkbox, IconButton, Typography } from '@mui/material';
import PersonRemoveOutlinedIcon from '@mui/icons-material/PersonRemoveOutlined';
import { toast } from 'react-toastify';
import { API } from '../../../../../../../api';
import { capabilityApi } from '../../../../../../../api/capability';
import ConfirmDialog from '../../../../../../../components/DialogUi/ConfirmDialog';
import DosenButton from '../../../../../../../components/ui/Buttons/DosenButton';
import DosenTooltip from '../../../../../../../components/ui/DosenTooltip';
import { useProgramContext } from '../../../../../../../hooks/useProgramContext';
import { useAppSelector } from '../../../../../../../redux/hooks';
import type { IGroupInvitedUser, IGroupUser } from '../../../../../../../redux/slices/group-users/groupUsersSlice';
import { capabilityColor } from '../teamCapability';
import type { ProgramCardData, ProgramOwner } from '../types';
import { Bar, Muted, ORANGE, Panel, t } from './editorUi';
import InvitePeopleDialog from './InvitePeopleDialog';
import {
  bulkRemovalMessage,
  bulkRemovalTitle,
  bulkRemovedToast,
  canRemove,
  isLearner,
  peopleCount,
  removalMessage,
  removalTitle,
  removedToast,
  removeEach
} from './peopleRemoval';

interface PersonRow {
  id: string;
  name: string;
  email: string;
  invited: boolean;
  /** Their roles in this program ("admin", "mentor", "mentee"); none for someone invited. */
  roles: string[];
  status: string;
  started: boolean;
  /** 0-100; null for someone invited, who has nothing yet (sorted last). */
  progress: number | null;
}

const COLS = '1.6fr 110px 130px 1.6fr';
// With Remove: a first, narrow column to tick the row and a last one for its action.
const COLS_WITH_ACTION = `28px ${COLS} 32px`;

const tickSx = {
  p: 0,
  color: t.textTertiary,
  '&.Mui-checked, &.MuiCheckbox-indeterminate': { color: ORANGE },
  '&.Mui-disabled': { color: t.borderDim }
};

const fullName = (u: { name?: string; firstName?: string; lastName?: string; email?: string }) =>
  u.name || `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim() || u.email || '';

const head = (label: string) => (
  <Typography
    sx={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: t.textTertiary }}
  >
    {label}
  </Typography>
);

const ProgramPeopleTab: React.FC<{
  program: ProgramCardData;
  orgMembers: ProgramOwner[];
  /** After someone is removed, so the board's card (its completion) is read again. */
  onPeopleChanged?: () => void;
}> = ({ program, orgMembers, onPeopleChanged }) => {
  const { orgId } = useProgramContext();
  const authId = useAppSelector((s) => s.orgDetails.data?.authId) || orgId;
  const viewerId = useAppSelector((s) => s.userProfile.data?._id) || '';
  const [users, setUsers] = useState<IGroupUser[] | null>(null);
  const [invited, setInvited] = useState<IGroupInvitedUser[]>([]);
  const [failed, setFailed] = useState(false);
  const [inviting, setInviting] = useState(false);
  // The org admins among the program's people: Remove is never offered on them, and waits until they are known.
  const [orgAdminIds, setOrgAdminIds] = useState<Set<string> | null>(null);
  // In a program for roles, the people who hold one of its roles (the confirm says they keep it).
  const [roleHolderIds, setRoleHolderIds] = useState<Set<string>>(new Set());
  // Whom Remove asks about. Kept while the confirm fades out, so its text doesn't empty first.
  const [removing, setRemoving] = useState<PersonRow | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [removeBusy, setRemoveBusy] = useState(false);
  // The rows ticked to remove at once, and its confirm.
  const [ticked, setTicked] = useState<Set<string>>(new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  // How many the confirm asks about, kept while it fades out.
  const [bulkCount, setBulkCount] = useState(0);

  // Fresh, not from the GET cache: it is read again right after an invite or a removal.
  const load = useCallback(() => {
    setFailed(false);
    API.getGroupUsers(program.id, 1, 200, undefined, { fresh: true })
      .then((res: any) => {
        const d = res?.data ?? {};
        setUsers(Array.isArray(d.users) ? d.users : []);
        setInvited(Array.isArray(d.invitedUsers) ? d.invitedUsers : []);
      })
      .catch(() => {
        setUsers([]);
        setFailed(true);
      });
  }, [program.id]);

  useEffect(() => {
    load();
  }, [load]);

  // Org Members for this program says who is an org admin (memberType "organization_admin").
  useEffect(() => {
    if (!program.canEdit || !authId) return;
    let cancelled = false;
    setOrgAdminIds(null);
    API.getOrgMembers(authId, program.id)
      .then((res: any) => {
        const members: any[] = Array.isArray(res?.data?.members) ? res.data.members : [];
        if (cancelled) return;
        setOrgAdminIds(new Set(members.filter((m) => m?.memberType === 'organization_admin').map((m) => String(m.id))));
      })
      // Unknown: Remove stays hidden rather than be offered on an org admin.
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [program.id, program.canEdit, authId]);

  // A program for roles: who holds one of its roles, from Capability Operations (as Invite People reads it).
  const programRoleKey = program.roleIds.join(',');
  useEffect(() => {
    if (!program.canEdit || !programRoleKey) return;
    let cancelled = false;
    const programRoles = new Set(programRoleKey.split(','));
    capabilityApi
      .operations(undefined, orgId)
      .then((view) => {
        if (cancelled) return;
        const holders = (view.people ?? []).filter((p) => !p.invitePending && p.roleId && programRoles.has(p.roleId));
        setRoleHolderIds(new Set(holders.map((p) => p.id)));
      })
      // Unknown: the confirm doesn't claim they keep a role.
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [program.canEdit, programRoleKey, orgId]);

  const rows: PersonRow[] = [
    ...(users ?? []).map((u): PersonRow => {
      const pct = Math.round(u.goalProgress ?? 0);
      return {
        id: u.userId,
        name: fullName(u),
        email: u.email ?? '',
        invited: false,
        roles: Array.isArray(u.role) ? u.role : [],
        started: pct > 0,
        status: pct >= 100 ? 'Completed' : pct > 0 ? 'In progress' : 'Not started',
        progress: pct
      };
    }),
    ...invited.map(
      (u): PersonRow => ({
        id: u.invitationId || u.userId || u.email,
        name: fullName(u),
        email: u.email ?? '',
        invited: true,
        roles: [],
        started: false,
        status: 'Invited',
        progress: null
      })
    )
  ];
  // Highest completion first; invited people, with nothing yet, last.
  const sorted = [...rows].sort((x, y) => (y.progress ?? -1) - (x.progress ?? -1));
  // Who can still be added: org members not already in the program.
  const inProgram = new Set((users ?? []).map((u) => u.userId));
  const choices = orgMembers.filter((m) => !inProgram.has(m.id));

  const removal = {
    canEdit: program.canEdit,
    viewerId,
    // The owner, or the creator who stands in for one on the card.
    ownerId: program.owner?.id || program.creator?.id || null,
    orgAdminIds
  };
  const cols = program.canEdit ? COLS_WITH_ACTION : COLS;
  const removable = sorted.filter((r) => canRemove(r, removal));
  // Only rows that can still be removed stay ticked (after a reload, say).
  const tickedRows = removable.filter((r) => ticked.has(r.id));
  const allTicked = removable.length > 0 && tickedRows.length === removable.length;
  const tick = (id: string) =>
    setTicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const tickAll = () => setTicked(allTicked ? new Set() : new Set(removable.map((r) => r.id)));

  const askRemove = (r: PersonRow) => {
    setRemoving(r);
    setConfirmOpen(true);
  };

  const remove = async () => {
    if (!removing) return;
    const who = removing;
    setRemoveBusy(true);
    try {
      await API.removeUserFromGroup({ grpId: program.id, userId: who.id });
      toast.success(removedToast(who.name, program.name));
      onPeopleChanged?.();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || `${who.name} couldn't be removed from ${program.name}. Try again.`);
    } finally {
      setRemoveBusy(false);
      setConfirmOpen(false);
      // Either way the list shows what the server now holds.
      load();
    }
  };

  const removeTicked = async () => {
    const who = tickedRows;
    if (!who.length) return;
    setBulkBusy(true);
    try {
      const { removed, failed } = await removeEach(
        who.map((r) => r.id),
        (id) => API.removeUserFromGroup({ grpId: program.id, userId: id })
      );
      if (removed.length) toast.success(bulkRemovedToast(removed.length, program.name));
      if (failed.length) {
        const names = who.filter((r) => failed.includes(r.id)).map((r) => r.name);
        toast.error(`${names.join(', ')} couldn't be removed from ${program.name}. Try again.`);
      }
      setTicked(new Set(failed));
      if (removed.length) onPeopleChanged?.();
    } finally {
      setBulkBusy(false);
      setBulkOpen(false);
      // Either way the list shows what the server now holds.
      load();
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <Panel sx={{ p: '18px 20px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: 18, fontWeight: 700, color: t.textPrimary, lineHeight: 1.3 }}>
              People{users ? ` (${rows.length})` : ''}
            </Typography>
            <Muted>
              Everyone in this program: the people its audience brought in, and anyone you invited. Invited people do
              not have to match the audience.
            </Muted>
          </Box>
          {program.canEdit && (
            <DosenButton tone="cta" sizeVariant="sm" sx={{ minWidth: 140 }} onClick={() => setInviting(true)}>
              Invite People
            </DosenButton>
          )}
        </Box>
        {users === null ? (
          <Muted>Loading people...</Muted>
        ) : failed ? (
          <Muted>The people in this program couldn’t be loaded. Close and reopen the program to try again.</Muted>
        ) : rows.length === 0 ? (
          <Muted>Nobody is in this program yet.</Muted>
        ) : (
          <>
            {tickedRows.length > 0 && (
              <Box
                role="region"
                aria-label="Selected people"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  p: '8px 12px',
                  borderRadius: '8px',
                  border: `1px solid ${ORANGE}`,
                  bgcolor: 'rgba(247, 148, 32, 0.08)'
                }}
              >
                <Typography sx={{ flex: 1, fontSize: 13, fontWeight: 600, color: t.textPrimary }}>
                  {peopleCount(tickedRows.length)} selected
                </Typography>
                <DosenButton tone="basic" sizeVariant="sm" onClick={() => setTicked(new Set())}>
                  Clear
                </DosenButton>
                <DosenButton
                  tone="cta"
                  sizeVariant="sm"
                  sx={{ minWidth: 140 }}
                  onClick={() => {
                    setBulkCount(tickedRows.length);
                    setBulkOpen(true);
                  }}
                >
                  Remove {peopleCount(tickedRows.length)}
                </DosenButton>
              </Box>
            )}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: cols,
                gap: '12px',
                alignItems: 'center',
                px: '12px',
                pt: '6px'
              }}
            >
              {program.canEdit && (
                <DosenTooltip
                  title={removable.length ? 'Select everyone you can remove' : 'Nobody here can be removed'}
                >
                  <span>
                    <Checkbox
                      size="small"
                      checked={allTicked}
                      indeterminate={tickedRows.length > 0 && !allTicked}
                      disabled={removable.length === 0}
                      onChange={tickAll}
                      inputProps={{ 'aria-label': 'Select everyone you can remove' }}
                      sx={tickSx}
                    />
                  </span>
                </DosenTooltip>
              )}
              {head('Person')}
              {head('Added by')}
              {head('Status')}
              {head('Completion')}
              {program.canEdit && <Box />}
            </Box>
            {sorted.map((r) => (
              <Box
                key={r.id}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: cols,
                  gap: '12px',
                  alignItems: 'center',
                  p: '12px',
                  borderRadius: '8px',
                  border: `1px solid ${t.borderDim}`,
                  bgcolor: '#151515'
                }}
              >
                {program.canEdit &&
                  (canRemove(r, removal) ? (
                    <Checkbox
                      size="small"
                      checked={ticked.has(r.id)}
                      onChange={() => tick(r.id)}
                      inputProps={{ 'aria-label': `Select ${r.name}` }}
                      sx={tickSx}
                    />
                  ) : (
                    <Box />
                  ))}
                <Box sx={{ minWidth: 0 }}>
                  <Typography noWrap sx={{ fontSize: 14, fontWeight: 600, color: t.textPrimary }}>
                    {r.name}
                  </Typography>
                  {r.email && (
                    <Typography noWrap sx={{ fontSize: 12, color: t.textTertiary }}>
                      {r.email}
                    </Typography>
                  )}
                </Box>
                <Typography sx={{ fontSize: 13, color: r.invited ? '#F79420' : t.textSecondary }}>
                  {r.invited ? 'Invited' : 'Audience'}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 13,
                    fontWeight: r.started ? 600 : 400,
                    color: r.started ? t.textPrimary : t.textTertiary
                  }}
                >
                  {r.status}
                </Typography>
                {r.progress === null ? (
                  <Typography sx={{ fontSize: 13, color: t.textTertiary }}>No result yet</Typography>
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', maxWidth: 260 }}>
                    <Bar percent={r.progress} />
                    <Typography
                      sx={{
                        fontSize: 13,
                        fontWeight: 600,
                        color: capabilityColor(r.progress),
                        minWidth: 40,
                        textAlign: 'right'
                      }}
                    >
                      {r.progress}%
                    </Typography>
                  </Box>
                )}
                {program.canEdit && (
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    {canRemove(r, removal) && (
                      <DosenTooltip title="Remove from this program">
                        <IconButton
                          onClick={() => askRemove(r)}
                          aria-label={`Remove ${r.name}`}
                          sx={{ width: 28, height: 28, color: t.textTertiary, '&:hover': { color: t.textPrimary } }}
                        >
                          <PersonRemoveOutlinedIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </DosenTooltip>
                    )}
                  </Box>
                )}
              </Box>
            ))}
          </>
        )}
      </Panel>
      <InvitePeopleDialog
        open={inviting}
        program={program}
        choices={choices}
        onClose={() => setInviting(false)}
        onInvited={load}
      />
      <ConfirmDialog
        open={confirmOpen}
        title={removing ? removalTitle(removing.name, program.name) : ''}
        message={
          removing
            ? removalMessage({
                // Added by: Audience, as the row reads.
                fromAudience: !removing.invited,
                holdsProgramRole: isLearner(removing.roles) && roleHolderIds.has(removing.id)
              })
            : ''
        }
        confirmLabel="Remove"
        cancelLabel="Cancel"
        busy={removeBusy}
        busyLabel="Removing..."
        onConfirm={remove}
        onClose={() => setConfirmOpen(false)}
      />
      <ConfirmDialog
        open={bulkOpen}
        title={bulkRemovalTitle(bulkCount, program.name)}
        message={bulkRemovalMessage()}
        confirmLabel={`Remove ${peopleCount(bulkCount)}`}
        cancelLabel="Cancel"
        busy={bulkBusy}
        busyLabel="Removing..."
        onConfirm={removeTicked}
        onClose={() => setBulkOpen(false)}
      />
    </Box>
  );
};

export default ProgramPeopleTab;
